import { ActionButton } from '@/components/common/ActionButton';
import { TOOLBAR_DROPDOWN_PT, TOOLBAR_LABEL_CLASSES } from '@/components/projects/toolbarDropdown';
import type { SortConfig } from '@/types/project.types';
import { SortAscending, SortDescending } from '@phosphor-icons/react';
import { Dropdown } from 'primereact/dropdown';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface SortControlsProps {
  sortConfig: SortConfig;
  onSortChange: (config: SortConfig) => void;
}

/**
 * "Sort by <field> <direction>" for the projects toolbar.
 *
 * The direction is a button rather than a third dropdown option because it is a toggle, and
 * because two selects side by side read as two unrelated settings rather than as one sentence.
 */
export const SortControls: FC<SortControlsProps> = ({ sortConfig, onSortChange }) => {
  const { t } = useTranslation();
  const sortFields = [
    { label: t('home.sortTitle'), value: 'title' },
    { label: t('home.sortDate'), value: 'created_at' },
  ];

  const ascending = sortConfig.order === 'asc';
  const directionLabel = ascending ? t('home.ascending') : t('home.descending');

  return (
    <div className="flex items-center gap-2">
      <span className={TOOLBAR_LABEL_CLASSES}>{t('home.sortBy')}</span>

      <Dropdown
        value={sortConfig.field}
        options={sortFields}
        onChange={(e) => onSortChange({ ...sortConfig, field: e.value })}
        className="w-32"
        pt={TOOLBAR_DROPDOWN_PT}
        aria-label={t('home.sortBy')}
      />

      <ActionButton
        variant="ghost"
        iconOnly={true}
        icon={
          ascending ? <SortAscending size={16} aria-hidden="true" /> : <SortDescending size={16} aria-hidden="true" />
        }
        onClick={() => onSortChange({ ...sortConfig, order: ascending ? 'desc' : 'asc' })}
        title={directionLabel}
        aria-label={directionLabel}
      />
    </div>
  );
};
